import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  // PayPal returns 'token' on approval redirect
  const paypalToken = params.get("token") || sessionStorage.getItem("davide_paypal_order_id");
  // Paystack returns 'reference' and 'trxref'
  const paystackRef = params.get("reference") || params.get("trxref") || sessionStorage.getItem("davide_paystack_ref");
  const [status, setStatus] = useState("pending");
  const [amount, setAmount] = useState(null);
  const [provider, setProvider] = useState(null);
  const { clear } = useCart();

  useEffect(() => {
    let cancelled = false;

    const handlePaystack = async () => {
      setProvider("paystack");
      try {
        const { data } = await api.get(`/paystack/verify/${paystackRef}`);
        if (cancelled) return;
        sessionStorage.removeItem("davide_paystack_ref");
        if (data.payment_status === "paid") {
          setStatus("paid"); setAmount(data.amount); clear();
        } else if (data.payment_status === "failed") {
          setStatus("failed");
        } else {
          setStatus("pending");
        }
      } catch {
        if (!cancelled) setStatus("error");
      }
    };

    const handlePayPal = async () => {
      setProvider("paypal");
      try {
        const { data } = await api.post(`/paypal/capture-order/${paypalToken}`);
        if (cancelled) return;
        sessionStorage.removeItem("davide_paypal_order_id");
        if (data.payment_status === "paid") {
          setStatus("paid"); setAmount(data.amount); clear();
        } else if (data.payment_status === "failed") {
          setStatus("failed");
        } else {
          setStatus("pending");
        }
      } catch {
        if (!cancelled) setStatus("error");
      }
    };

    const pollStripe = async () => {
      setProvider("stripe");
      let attempts = 0;
      const tick = async () => {
        if (cancelled) return;
        attempts += 1;
        try {
          const { data } = await api.get(`/checkout/status/${sessionId}`);
          if (data.payment_status === "paid") {
            setStatus("paid"); setAmount(data.amount); clear(); return;
          }
          if (data.status === "expired") { setStatus("expired"); return; }
          if (attempts >= 8) { setStatus("pending"); return; }
          setTimeout(tick, 2000);
        } catch {
          if (attempts >= 8) setStatus("error");
          else setTimeout(tick, 2000);
        }
      };
      tick();
    };

    // Priority: Paystack (has reference param) > PayPal (token) > Stripe (session_id)
    if (paystackRef && !sessionId) handlePaystack();
    else if (paypalToken && !sessionId) handlePayPal();
    else if (sessionId) pollStripe();
    else setStatus("error");
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId, paypalToken, paystackRef]);

  return (
    <div className="pt-40 pb-24 min-h-screen flex items-center justify-center px-6" data-testid="checkout-success-page">
      <div className="max-w-xl w-full text-center border border-white/10 bg-[#101010] p-12">
        {status === "paid" && <>
          <CheckCircle2 size={56} className="text-[var(--davide-gold)] mx-auto mb-6" />
          <h1 className="font-display text-5xl text-white mb-4">Asé.</h1>
          <p className="text-white/70 leading-relaxed">
            Your order is confirmed{amount ? ` — $${Number(amount).toFixed(2)}` : ""}
            {provider ? ` via ${provider === "paypal" ? "PayPal" : provider === "paystack" ? "Paystack" : "Stripe"}` : ""}.
            A receipt has been emailed. Your DaviDÉ pieces will ship within 2 business days.
          </p>
        </>}
        {status === "pending" && <>
          <Clock size={56} className="text-[var(--davide-gold)] mx-auto mb-6 animate-pulse" />
          <h1 className="font-display text-4xl text-white mb-4">Confirming your order…</h1>
          <p className="text-white/70">Hold tight. We're verifying your payment.</p>
        </>}
        {status === "expired" && <>
          <XCircle size={56} className="text-[var(--davide-red)] mx-auto mb-6" />
          <h1 className="font-display text-4xl text-white mb-4">Session expired.</h1>
          <p className="text-white/70">Please return to checkout and try again.</p>
        </>}
        {status === "failed" && <>
          <XCircle size={56} className="text-[var(--davide-red)] mx-auto mb-6" />
          <h1 className="font-display text-4xl text-white mb-4">Payment failed.</h1>
          <p className="text-white/70">Your card was not charged. Please try again.</p>
        </>}
        {status === "error" && <>
          <XCircle size={56} className="text-[var(--davide-red)] mx-auto mb-6" />
          <h1 className="font-display text-4xl text-white mb-4">Something went wrong.</h1>
          <p className="text-white/70">We couldn't verify your payment status. Please check your email or contact support.</p>
        </>}
        <Link to="/shop" className="btn-outline-gold inline-block mt-10" data-testid="back-to-shop-btn">Continue Shopping</Link>
      </div>
    </div>
  );
}
